import type { SkillBranchId } from "../content/schemas";
import type { LetterGrade } from "./scoring";

export const MAX_PILOT_RANK = 100;
export const RP_BASE_PER_RANK = 120;

export type BranchMastery = Record<SkillBranchId, number>;

export type PilotMeta = {
  rank: number;
  rankPoints: number;
  rankPointsToNext: number;
  totalRankPointsEarned: number;
  branchMastery: BranchMastery;
  titles: string[];
  achievements: string[];
  personalBestScore: number;
  personalBestGrade: LetterGrade;
};

export type RunHistoryEntry = {
  outcome: "extracted" | "dead";
  score: number;
  level: number;
  grade: LetterGrade;
  simTime: number;
  at: string;
};

export function createPilotMeta(): PilotMeta {
  return {
    rank: 1,
    rankPoints: 0,
    rankPointsToNext: rpRequiredForRank(1),
    totalRankPointsEarned: 0,
    branchMastery: emptyMastery(),
    titles: ["Rookie"],
    achievements: [],
    personalBestScore: 0,
    personalBestGrade: "D",
  };
}

export function emptyMastery(): BranchMastery {
  return { hull: 0, shield: 0, weapons: 0, mobility: 0, utility: 0 };
}

export function rpRequiredForRank(rank: number): number {
  if (rank >= MAX_PILOT_RANK) return 0;
  return RP_BASE_PER_RANK + rank * 40;
}

export function rankPointsForOutcome(
  outcome: "extracted" | "dead",
  score: number,
  level: number,
): number {
  if (outcome === "extracted") return Math.floor(score / 500) + level * 2;
  return Math.floor(score / 1000);
}

export function applyRankPoints(meta: PilotMeta, gained: number): number {
  if (gained <= 0 || meta.rank >= MAX_PILOT_RANK) return 0;
  // Rank 50 was the old cap and persisted with a zero threshold. Reopen that
  // profile safely before adding RP so it cannot skip Rank 51 for free.
  if (meta.rankPointsToNext <= 0) meta.rankPointsToNext = rpRequiredForRank(meta.rank);
  meta.rankPoints += gained;
  meta.totalRankPointsEarned += gained;
  let levels = 0;
  while (meta.rank < MAX_PILOT_RANK && meta.rankPoints >= meta.rankPointsToNext) {
    meta.rankPoints -= meta.rankPointsToNext;
    meta.rank += 1;
    levels += 1;
    meta.rankPointsToNext = rpRequiredForRank(meta.rank);
    maybeGrantTitle(meta);
  }
  if (meta.rank >= MAX_PILOT_RANK) {
    meta.rankPoints = 0;
    meta.rankPointsToNext = 0;
  }
  return levels;
}

function maybeGrantTitle(meta: PilotMeta): void {
  const titles: Record<number, string> = {
    5: "Veteran",
    10: "Ace",
    20: "Elite",
    35: "Velocity",
    50: "Legend",
    75: "Pathfinder",
    100: "Centurion",
  };
  const title = titles[meta.rank];
  if (title && !meta.titles.includes(title)) meta.titles.push(title);
}

export function hasRerollUnlock(meta: PilotMeta): boolean {
  return meta.rank >= 5;
}

export function rerollCharges(meta: PilotMeta): number {
  if (meta.rank >= 15) return 2;
  if (meta.rank >= 5) return 1;
  return 0;
}

export function hasBanishUnlock(meta: PilotMeta): boolean {
  return meta.rank >= 10;
}

export function recordPersonalBest(meta: PilotMeta, score: number, grade: LetterGrade): boolean {
  if (score <= meta.personalBestScore) return false;
  meta.personalBestScore = score;
  meta.personalBestGrade = grade;
  return true;
}

export function addBranchMastery(meta: PilotMeta, branch: SkillBranchId, amount = 1): void {
  meta.branchMastery[branch] = (meta.branchMastery[branch] ?? 0) + amount;
}
